/**
 * Shared SplitText headline + aside + body tweens for section headers.
 * Callers own the timeline's ScrollTrigger (hero runway vs chapter scrub) and
 * pass the timing config (`introHeaderReveal` or `sectionHeaderReveal`).
 */

import {
  SECTION_ASIDE_REVEAL_SELECTOR,
  SECTION_ASIDE_SCALE_SELECTOR,
  SECTION_BODY_REVEAL_SELECTOR,
  SECTION_HEADLINE_MASK_PADDING,
  SECTION_HEADLINE_SELECTOR,
} from '@/constants/sectionHeaderReveal';
import { shouldRevealHeadlineByWord } from '@/motion/headlineRevealUnits';

import type { SectionHeaderContentTiming } from '@/config/section-header-reveal';
import type { ScrollSceneRuntime } from '@/motion/scrollScene';

type SectionHeaderContentOptions = {
  /** Query scope — never document-wide, so intro and chapters don't clash. */
  root: HTMLElement;
  /** Timeline to append tweens to (caller attaches ScrollTrigger). */
  timeline: gsap.core.Timeline;
  /** Intro or chapter timing knobs. */
  timing: SectionHeaderContentTiming;
  /**
   * Absolute time where the headline segment should start. Intro passes
   * `timeline.duration()` after chrome; chapter scrubs pass `0`.
   */
  headlineAt?: number;
};

export type SectionHeaderContentResult = {
  /** True when at least one headline / aside / body target was found. */
  hasTargets: boolean;
  cleanup: () => void;
};

function clearWillChange(elements: Element[]) {
  for (const el of elements) {
    if (el instanceof HTMLElement) {
      el.style.removeProperty('will-change');
    }
  }
}

function setWillChange(elements: Element[]) {
  for (const el of elements) {
    if (el instanceof HTMLElement) {
      el.style.setProperty('will-change', 'transform');
    }
  }
}

function headlineTransformOrigin(timing: SectionHeaderContentTiming): string {
  return document.documentElement.dir === 'rtl'
    ? timing.headline.transformOriginRtl
    : timing.headline.transformOriginLtr;
}

/**
 * Append SplitText masked headline units (+ aside in parallel) → body fade
 * onto an existing timeline. Targets are queried inside `root` only.
 */
export function appendSectionHeaderContent(
  { gsap, SplitText }: ScrollSceneRuntime,
  { root, timeline, timing, headlineAt = 0 }: SectionHeaderContentOptions,
): SectionHeaderContentResult {
  const headlineEls = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(SECTION_HEADLINE_SELECTOR),
  );
  const asideTargets = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(SECTION_ASIDE_REVEAL_SELECTOR),
  );
  const asideScaleTargets = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(SECTION_ASIDE_SCALE_SELECTOR),
  );
  const bodyTargets = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(SECTION_BODY_REVEAL_SELECTOR),
  );

  if (
    headlineEls.length === 0 &&
    asideTargets.length === 0 &&
    asideScaleTargets.length === 0 &&
    bodyTargets.length === 0
  ) {
    return { hasTargets: false, cleanup: () => undefined };
  }

  let split: InstanceType<ScrollSceneRuntime['SplitText']> | null = null;
  /** Reveal units grouped per headline element — each line staggers in parallel. */
  let unitsByLine: Element[][] = [];
  let allHeadlineUnits: Element[] = [];
  /** Parent heading named for AT; line spans are aria-hidden after split. */
  let namedHeadline: HTMLHeadingElement | null = null;

  if (headlineEls.length > 0) {
    // Line spans are role=generic — aria-label is invalid there. Name the
    // parent h2 and hide the split trees so AT doesn't hear letter salad.
    namedHeadline = headlineEls[0]?.closest('h2') ?? null;
    if (namedHeadline) {
      const label = headlineEls
        .map((el) => (el.textContent ?? '').trim())
        .filter(Boolean)
        .join(' ');
      if (label) {
        namedHeadline.setAttribute('aria-label', label);
      }
    }
    for (const el of headlineEls) {
      el.setAttribute('aria-hidden', 'true');
    }

    const revealByWord = shouldRevealHeadlineByWord();
    split = SplitText.create(headlineEls, {
      // Arabic words remain intact so contextual glyph shaping is preserved.
      // Latin locales retain the existing per-character choreography.
      type: revealByWord ? 'words,lines' : 'chars,words,lines',
      mask: timing.headline.mask,
      aria: 'none',
    });
    allHeadlineUnits = revealByWord ? split.words : split.chars;
    // Group by original headline element (filled / outlined), not SplitText
    // soft-wrap lines — so both words start together even if a long word wraps.
    unitsByLine = headlineEls.map((el) =>
      allHeadlineUnits.filter((unit) => el.contains(unit)),
    );
    // title-l uses line-height: 1; Sora caps overshoot that box (~0.2em). Pad
    // overflow:clip masks so ascenders/descenders aren't sheared. Arabic's
    // below-baseline marks need extra block-end paint room.
    if (split.masks.length > 0) {
      const paddingBottom =
        document.documentElement.dir === 'rtl'
          ? SECTION_HEADLINE_MASK_PADDING.rtlBlockEnd
          : SECTION_HEADLINE_MASK_PADDING.blockEnd;
      gsap.set(split.masks, {
        paddingTop: SECTION_HEADLINE_MASK_PADDING.blockStart,
        paddingBottom,
        marginTop: `-${SECTION_HEADLINE_MASK_PADDING.blockStart}`,
        marginBottom: `-${paddingBottom}`,
      });
    }
    // Clear CSS pre-hide on the parent; units stay invisible until the headline
    // segment so nothing is static during an earlier chrome scrub (intro).
    gsap.set(headlineEls, { autoAlpha: 1, y: 0 });
    for (const units of unitsByLine) {
      gsap.set(units, {
        transformOrigin: headlineTransformOrigin(timing),
        autoAlpha: 0,
        yPercent: timing.headline.yPercent,
        y: (i: number) => i * timing.headline.staggerOffsetYPx,
        rotation: timing.headline.rotationDeg,
      });
    }
  }

  timeline.addLabel('sectionHeadline', headlineAt);
  const hasHeadlineSegment =
    allHeadlineUnits.length > 0 ||
    asideTargets.length > 0 ||
    asideScaleTargets.length > 0;

  // One tween per authored headline line so filled and outlined lines start
  // together. Latin characters or intact Arabic words stagger within each.
  for (const units of unitsByLine) {
    if (units.length === 0) continue;
    timeline.fromTo(
      units,
      {
        autoAlpha: 0,
        yPercent: timing.headline.yPercent,
        y: (i: number) => i * timing.headline.staggerOffsetYPx,
        rotation: timing.headline.rotationDeg,
      },
      {
        autoAlpha: 1,
        yPercent: 0,
        y: 0,
        rotation: 0,
        ease: timing.headline.ease,
        duration: timing.headline.weight,
        stagger: {
          each: timing.headline.stagger,
          from: 'start',
        },
        onStart: () => setWillChange(units),
        onComplete: () => clearWillChange(units),
        onReverseComplete: () => clearWillChange(units),
      },
      'sectionHeadline',
    );
  }

  if (asideTargets.length > 0) {
    // The full aside keeps its authored fade/rise; scale belongs to the nested
    // stat target so bird artwork, captions, and links retain their dimensions.
    timeline.fromTo(
      asideTargets,
      {
        autoAlpha: 0,
        y: timing.aside.offsetY,
      },
      {
        autoAlpha: 1,
        y: 0,
        ease: timing.aside.ease,
        duration: timing.headline.weight,
      },
      'sectionHeadline',
    );
  }

  if (asideScaleTargets.length > 0) {
    timeline.fromTo(
      asideScaleTargets,
      { scale: timing.aside.fromScale },
      {
        scale: 1,
        ease: timing.aside.ease,
        duration: timing.headline.weight,
        transformOrigin: '50% 50%',
      },
      'sectionHeadline',
    );
  }

  // Body (pin + prose): fade starting just before the headline segment finishes.
  if (bodyTargets.length > 0) {
    const maxUnitsInLine = unitsByLine.reduce(
      (max, units) => Math.max(max, units.length),
      0,
    );
    const headlineSegmentDuration =
      maxUnitsInLine > 0
        ? timing.headline.weight +
          timing.headline.stagger * Math.max(0, maxUnitsInLine - 1)
        : hasHeadlineSegment
          ? timing.headline.weight
          : 0;
    const early = Math.min(Math.max(timing.body.earlyByHeadlineFraction, 0), 1);
    const bodyAt = hasHeadlineSegment
      ? headlineAt + headlineSegmentDuration * (1 - early)
      : headlineAt;

    timeline.fromTo(
      bodyTargets,
      { autoAlpha: 0, y: timing.body.offsetY },
      {
        autoAlpha: 1,
        y: 0,
        ease: timing.body.ease,
        duration: timing.body.weight,
      },
      bodyAt,
    );
  }

  return {
    hasTargets: true,
    cleanup: () => {
      clearWillChange(allHeadlineUnits);
      if (split) {
        split.revert();
        split = null;
      }
      if (namedHeadline) {
        namedHeadline.removeAttribute('aria-label');
        namedHeadline = null;
      }
      for (const el of headlineEls) {
        el.removeAttribute('aria-hidden');
      }
    },
  };
}
